import { Group, lighten, Loader, Stack } from "@mantine/core";
import { AdvizerVideoCard } from "@/components/AdvizerVideoCard";
import { AdvizerSearchControls } from "@/components/advizerSearchControls/AdvizerSearchControls";
import { useIsLaptopOrBigger, useStyles } from "@/styles/useStyles";
import {
  useArrayOfSelectedAdvizerSearchFilters,
  useFilteredAdvizers,
  useIsAdvizersFiltered,
  useIsApiDown,
  useIsLoggedIn,
  useIsSearchingAdvizers,
} from "@/store/selectors";
import { AdvizerSearchFilters } from "@/components/AdvizerSearchFilters";
import { FC, useCallback, useMemo, useState } from "react";
import { useStore, useValueFromBreakpoint } from "@/hooks";
import { ErrorOrNoResults } from "@/components/templates/ErrorOrNoResults";
import { Title } from "@/components/ui/Title";
import { Text } from "@/components/ui/Text";
import { useLocation } from "react-router-dom";
import {
  FixedSizeList as _FixedSizeList,
  FixedSizeListProps,
} from "react-window";
import { AutoSizer as _AutoSizer, AutoSizerProps } from "react-virtualized";
import { LawHubBanner } from "@/components/ads/LawHubBanner";
import { config } from "@/config";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { usePartnerUserHasAnyAssignments } from "@/store/selectors/assignmentSelectors";
import { AssignmentManager } from "@/components/assignments/AssignmentManager";
import { AssignmentDefaultContent } from "./AssignmentDefaultContent";
import { PartnerDashboardLogic } from "@/components/logic/PartnerDashboardLogic";
import { useDemoMode } from "@/store/selectors/featureSelectors";
const AutoSizer = _AutoSizer as unknown as FC<AutoSizerProps>;
const FixedSizeList = _FixedSizeList as unknown as FC<FixedSizeListProps>;

// TODO use virtualized list
export const AdvizerSearchPage = () => {
  const { ...styles } = useStyles();
  const isLaptopOrBigger = useIsLaptopOrBigger();
  const isApiDown = useIsApiDown();
  const arrayOfSelectedAdvizerSearchFilters =
    useArrayOfSelectedAdvizerSearchFilters();
  const isInlineAdvizerSearchOpen = useStore(
    (state) => state.advizerSearch.isInlineAdvizerSearchOpen,
  );
  const collapseHeader = useStore(
    (state) => state.advizerSearch.collapseHeader,
  );
  const setCollapseHeader = useStore(
    (state) => state.advizerSearch.setCollapseHeader,
  );

  const { advizers, isAdvizersLoading } = useFilteredAdvizers();
  const { theme } = useStyles();
  const demoMode = useDemoMode();

  const isAdvizersFiltered = useIsAdvizersFiltered();
  const { isSearchingAdvizers } = useIsSearchingAdvizers();
  const isExplorePath = useLocation()?.pathname === "/explore";
  const isLoggedIn = useIsLoggedIn();
  const advizerVideoCardHeight = useValueFromBreakpoint({
    mobile: 800,
    tablet: 850,
    laptop: 500,
    desktop: 550,
  });

  const maxVideoCardWidth = useValueFromBreakpoint({
    mobile: 500,
    tablet: 650,
    laptop: 1050,
    desktop: 1200,
  });

  const showLawHubBanner = useMemo(() => {
    return false;
    return arrayOfSelectedAdvizerSearchFilters.some(
      (f) =>
        f.title.toLowerCase().includes("law") ||
        f.title.toLowerCase().includes("legal"),
    );
  }, [arrayOfSelectedAdvizerSearchFilters]);

  const showMainBanner = useMemo(
    () =>
      showLawHubBanner ||
      (!isExplorePath &&
        !isAdvizersFiltered &&
        !isLoggedIn &&
        !config.isPartnerApp &&
        !config.allowUnauthenticatedVideoPlayback),
    [isAdvizersFiltered, isExplorePath, isLoggedIn, showLawHubBanner],
  );

  // const MainTitleText = useMemo(() => {
  //   return (
  //     <Stack align="center" justify="center" gap="lg" maw={600}>
  //       <Avatar
  //         src={emilyAvatar}
  //         size={100}
  //         alt="Emily, CEO of Advize"
  //         style={{}}
  //       />
  //       <Title order={1} size="h3" lh={1.3} fw={500}>
  //         {LANDING_TEXT}
  //       </Title>
  //     </Stack>
  //   );
  // }, []);

  const renderMainBanner = useCallback(
    ({ style, key }: { style: any; key: string }) => {
      if (!(showMainBanner && showLawHubBanner)) return null;

      return (
        <Group
          id={"banner"}
          align="stretch"
          justify="center"
          key={key}
          p="md"
          flex={1}
          w="100%"
          style={{
            ...style,
            overflow: "hidden",
          }}
          gap={0}
        >
          <LawHubBanner
            stacked
            context="advizer_search"
            maxWidth={maxVideoCardWidth}
          />
          {/* <Stack
            h="100%"
            justify="center"
            align="center"
            ta="center"
            maw={600}
            gap="xs"
            p="xs"
          >
            <Group w="100%" gap={6} justify="center" align="center">
              {MainTitleText}
            </Group>

            <Space h="xs" />

            <Stack
              align="center"
              justify="center"
              style={{ fontSize: 44 }}
              gap={2}
            >
              <IconArrowDown
                size={32}
                color={theme.colors.primary[7]}
                className="pulse-animation"
              />
            </Stack>
          </Stack> */}
        </Group>
      );
    },
    [showLawHubBanner, showMainBanner],
  );

  const rowRenderer = useCallback(
    ({ index, style }: { index: number; style: any }) => {
      if (showMainBanner && index === 0) {
        return renderMainBanner({ style, key: String(index) });
      }

      const advizer = advizers?.[showMainBanner ? index - 1 : index];
      if (!advizer) return null;

      return (
        <Group
          id={String(index)}
          align="start"
          justify="center"
          key={advizer.id}
          pt={0}
          w="100%"
          style={{
            ...style,
            overflow: "hidden",
          }}
          gap={0}
        >
          <AdvizerVideoCard
            advizerId={advizer.id}
            advizer={advizer}
            maxWidth={maxVideoCardWidth}
          />
        </Group>
      );
    },
    [showMainBanner, advizers, maxVideoCardWidth, renderMainBanner],
  );

  const renderFailedApi = useCallback(() => {
    return (
      <Stack mih={400} h="50vh" align="center" justify="center" pt="xl" gap={4}>
        <Title order={1} size="h2" c="text.9">
          Shoot, something went wrong 😖
        </Title>
        <Text c="text.9">
          We're working on it and will be back up as soon as possible!
        </Text>
      </Stack>
    );
  }, []);

  const renderAdvizers = useCallback(() => {
    if (
      isAdvizersLoading ||
      isSearchingAdvizers ||
      (!isAdvizersFiltered && !advizers.length)
    ) {
      return (
        <Stack mih={400} h="50vh" align="center" justify="center" pt="xl">
          <Loader color="primary.5" size="xl" />
          <Title order={1} size="h2" c="text.7">
            Loading Advizers...
          </Title>
        </Stack>
      );
    }

    if (!advizers?.length) {
      return <ErrorOrNoResults />;
    }

    // TODO get tallest one in list, just force row height and card height to be same
    return (
      <Stack flex={1} w="100%" align="flex-start">
        <AutoSizer>
          {({ width, height }) => (
            <FixedSizeList
              width={width}
              height={height}
              itemCount={advizers?.length + (showMainBanner ? 1 : 0) || 0}
              itemSize={advizerVideoCardHeight}
              onScroll={(props) => {
                if (
                  props.scrollDirection === "forward" &&
                  props.scrollOffset > styles.dimensions.headerHeight
                ) {
                  setCollapseHeader(true);
                } else {
                  setCollapseHeader(false);
                }
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {rowRenderer}
            </FixedSizeList>
          )}
        </AutoSizer>
      </Stack>
    );
  }, [
    advizerVideoCardHeight,
    advizers.length,
    isAdvizersFiltered,
    isAdvizersLoading,
    isSearchingAdvizers,
    rowRenderer,
    setCollapseHeader,
    showMainBanner,
    styles.dimensions.headerHeight,
  ]);

  const isAssignmentManagerOpen = useStore(
    (state) => state.partnerApp.isAssignmentManagerOpen,
  );
  const partnerUserHasAnyAssignments = usePartnerUserHasAnyAssignments();
  const [hoverResizeHandle, setHoverResizeHandle] = useState(false);

  const RightDrawerContent = useMemo(() => {
    if (!isAssignmentManagerOpen) return null;

    if (partnerUserHasAnyAssignments) {
      return <AssignmentManager />;
    }

    return <AssignmentDefaultContent />;
  }, [isAssignmentManagerOpen, partnerUserHasAnyAssignments]);

  if (config.isPartnerApp || demoMode === "partner") {
    return (
      <>
        <Group
          // pt={collapseHeader ? 0 : styles.dimensions.headerHeight}
          gap={0}
          align="start"
          h="100vh"
          mah="100vh"
        >
          {isLaptopOrBigger ? (
            <Stack
              h={`calc(100vh - ${
                collapseHeader ? 0 : styles.dimensions.headerHeight
              }px)`}
              w={isInlineAdvizerSearchOpen ? 400 : 0}
              maw={isInlineAdvizerSearchOpen ? 400 : 0}
              miw={0}
              flex={isInlineAdvizerSearchOpen ? 1 : "0 0 auto"}
              pos="sticky"
              top={collapseHeader ? 0 : styles.dimensions.headerHeight - 10}
              style={{
                zIndex: 10,
                borderRight: `${isInlineAdvizerSearchOpen ? 2 : 0}px solid ${theme.colors.border[3]}`,
                overflow: "hidden",
                transition: "width 0.3s ease-in-out, max-width 0.3s ease-in-out, border-right-width 0.3s ease-in-out, opacity 0.3s ease-in-out",
                opacity: isInlineAdvizerSearchOpen ? 1 : 0,
                pointerEvents: isInlineAdvizerSearchOpen ? "auto" : "none",
              }}
              gap={0}
              p={0}
              pt={10}
            >
              <AdvizerSearchFilters />
            </Stack>
          ) : null}
          <PanelGroup
            direction="horizontal"
            style={{ flex: 2 }}
            // style={{
            //   height: "100vh",
            //   maxHeight: "100vh",
            //   paddingTop: collapseHeader ? 0 : styles.dimensions.headerHeight,
            // }}
          >
            <Panel minSize={30}>
              <Stack p={0} gap={0} flex={2} mah="100vh">
                <AdvizerSearchControls />
                <Stack
                  align="center"
                  w="100%"
                  mih={"100vh"}
                  gap="lg"
                  bg="background.0"
                  pt={collapseHeader ? 0 : styles.dimensions.headerHeight}
                >
                  {isApiDown ? renderFailedApi() : renderAdvizers()}
                </Stack>
              </Stack>
            </Panel>
            {RightDrawerContent && (
              <PanelResizeHandle
                id="right-drawer-resize-handle"
                onMouseEnter={() => setHoverResizeHandle(true)}
                onMouseLeave={() => setHoverResizeHandle(false)}
                style={{
                  width: 4,
                  position: "relative",
                  backgroundColor: hoverResizeHandle
                    ? theme.colors.primary[4]
                    : theme.colors.border[3],
                  borderRadius: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "col-resize",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "40%",
                    // transform: "translateY(-50%)",
                    right: -5,
                    width: 6,
                    height: "20%",
                    backgroundColor: hoverResizeHandle
                      ? theme.colors.primary[4]
                      : theme.colors.border[3],
                    borderTopRightRadius: 10,
                    borderBottomRightRadius: 10,
                    cursor: "col-resize",
                    zIndex: 100,
                    // opacity: hoverResizeHandle ? 1 : 0,
                  }}
                />
              </PanelResizeHandle>
            )}
            {RightDrawerContent ? (
              <Panel defaultSize={40} minSize={20}>
                <Stack
                  h={`calc(100vh - ${collapseHeader ? 0 : 0}px)`}
                  justify="flex-start"
                  align="flex-start"
                  // maw={450}
                  bg={lighten(theme.colors.background[1], 0.3)}
                  // flex={1}
                  pos="sticky"
                  // top={collapseHeader ? 0 : styles.dimensions.headerHeight - 10}
                  top={collapseHeader ? 0 : 0}
                  pb="lg"
                  style={{
                    zIndex: 10,
                    overflow: "auto",
                    // borderLeft: `4px solid ${theme.colors.border[3]}`,
                    // boxShadow: `-4px 0 8px -2px rgba(0, 0, 0, 0.15)`,
                  }}
                  gap={0}
                  p={0}
                  pt={collapseHeader ? 0 : styles.dimensions.headerHeight}
                >
                  {RightDrawerContent}
                </Stack>
              </Panel>
            ) : null}
          </PanelGroup>
        </Group>
        <PartnerDashboardLogic />
      </>
    );
  }

  return (
    <Group
      pt={collapseHeader ? 0 : styles.dimensions.headerHeight}
      gap={0}
      align="start"
      h="100vh"
      mah="100vh"
    >
      {isLaptopOrBigger ? (
        <Stack
          h={`calc(100vh - ${
            collapseHeader ? 0 : styles.dimensions.headerHeight
          }px)`}
          w={isInlineAdvizerSearchOpen ? 400 : 0}
          maw={isInlineAdvizerSearchOpen ? 400 : 0}
          miw={0}
          flex={isInlineAdvizerSearchOpen ? 1 : "0 0 auto"}
          pos="sticky"
          top={collapseHeader ? 0 : styles.dimensions.headerHeight - 10}
          style={{
            zIndex: 10,
            overflow: "hidden",
            transition: "width 0.3s ease-in-out, max-width 0.3s ease-in-out, opacity 0.3s ease-in-out",
            opacity: isInlineAdvizerSearchOpen ? 1 : 0,
            pointerEvents: isInlineAdvizerSearchOpen ? "auto" : "none",
          }}
          gap={0}
          p={0}
        >
          <AdvizerSearchFilters />
        </Stack>
      ) : null}
      <Stack
        p={0}
        gap={0}
        flex={2}
        mah="100vh"
        style={{
          transition: "margin-left 0.3s ease-in-out",
        }}
      >
        <AdvizerSearchControls />
        <Stack align="center" w="100%" mih={"100vh"} gap="lg" bg="background.0">
          {isApiDown ? renderFailedApi() : renderAdvizers()}
        </Stack>
      </Stack>
    </Group>
  );
};
